// WEEKEND WORK API - Frontend API integration for weekend work requests.
// Phase 8: Weekend Work Approval

import axios from 'axios'
import {setupAxios} from '../app/modules/auth/core/AuthHelpers'
import {API_BASE_URL} from '../utils/contants'

// =================== TYPES ===================

export interface WeekendWorkRequest {
  id: string
  userId: string
  requestDate: string
  reason: string
  estimatedHours: number
  projectCodeId?: string
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED'
  requestedAt: string
  reviewedById?: string
  reviewedAt?: string
  approvalRemarks?: string
  rejectionReason?: string
  compOffGranted: boolean
  compOffHours?: number
  compOffReason?: string
  createdAt: string
  updatedAt: string
  user?: {
    id: string
    employeeCode: string
    firstName: string
    lastName: string
    email: string
  }
  reviewer?: {
    id: string
    firstName: string
    lastName: string
  }
  projectCode?: {
    id: string
    code: string
    name: string
    type: string
  }
}

export interface WeekendWorkRequestInput {
  userId: string
  requestDate: string
  reason: string
  estimatedHours: number
  projectCodeId?: string
}

export interface WeekendWorkApproveInput {
  reviewedById: string
  approvalRemarks?: string
  compOffGranted?: boolean
  compOffHours?: number
  compOffReason?: string
}

export interface WeekendWorkRejectInput {
  reviewedById: string
  rejectionReason: string
}

export interface WeekendWorkCancelInput {
  userId: string
}

export interface WeekendWorkSummary {
  total: number
  pending: number
  approved: number
  rejected: number
}


// =================== COMP-OFF ===================

export interface CompOffEligibilityResponse {
  eligible: boolean
  reason?: string
}

export interface CompOffRequestInput {
  date: string
  hours: number
  reason: string
}


// =================== CREATE REQUEST ===================

export const createWeekendWorkRequest = async (data: WeekendWorkRequestInput) => {
  const {token} = setupAxios()
  return axios.post(`${API_BASE_URL}/weekend-work`, data, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

// =================== APPROVE REQUEST ===================

export const approveWeekendWorkRequest = async (requestId: string, data: WeekendWorkApproveInput) => {
  const {token} = setupAxios()
  return axios.post(`${API_BASE_URL}/weekend-work/${requestId}/approve`, data, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

// =================== REJECT REQUEST ===================

export const rejectWeekendWorkRequest = async (requestId: string, data: WeekendWorkRejectInput) => {
  const {token} = setupAxios()
  return axios.post(`${API_BASE_URL}/weekend-work/${requestId}/reject`, data, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

// =================== CANCEL REQUEST ===================

export const cancelWeekendWorkRequest = async (requestId: string, data: WeekendWorkCancelInput) => {
  const {token} = setupAxios()
  return axios.delete(`${API_BASE_URL}/weekend-work/${requestId}/cancel`, {
    headers: {Authorization: `Bearer ${token}`},
    data,
  })
}

// =================== GET PENDING REQUESTS (MANAGER) ===================

export const getPendingWeekendWorkRequests = async (filters: {
  managerId?: string
  startDate?: string
  endDate?: string
  userId?: string
  status?: string
  page?: number
  pageSize?: number
}) => {
  const {token} = setupAxios()
  const params = new URLSearchParams()
  if (filters.managerId) params.append('managerId', filters.managerId)
  if (filters.startDate) params.append('startDate', filters.startDate)
  if (filters.endDate) params.append('endDate', filters.endDate)
  if (filters.userId) params.append('userId', filters.userId)
  if (filters.status) params.append('status', filters.status)
  if (filters.page !== undefined) params.append('page', String(filters.page))
  if (filters.pageSize !== undefined) params.append('pageSize', String(filters.pageSize))

  return axios.get(`${API_BASE_URL}/weekend-work/pending?${params.toString()}`, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

// =================== GET USER REQUESTS ===================

export const getUserWeekendWorkRequests = async (filters: {
  userId: string
  startDate?: string
  endDate?: string
  status?: string
}) => {
  const {token} = setupAxios()
  const params = new URLSearchParams()
  if (filters.startDate) params.append('startDate', filters.startDate)
  if (filters.endDate) params.append('endDate', filters.endDate)
  if (filters.status) params.append('status', filters.status)

  return axios.get(`${API_BASE_URL}/weekend-work/user/${filters.userId}?${params.toString()}`, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

// =================== GET SINGLE REQUEST ===================

export const getWeekendWorkRequest = async (requestId: string) => {
  const {token} = setupAxios()
  return axios.get(`${API_BASE_URL}/weekend-work/${requestId}`, {
    headers: {Authorization: `Bearer ${token}`},
  })
}

// =================== CHECK COMP-OFF ELIGIBILITY ===================

export const checkCompOffEligibility = async (date: string) => {
  const {token} = setupAxios()

  return axios.get(`${API_BASE_URL}/weekend-work/comp-off/eligibility`, {
    headers: {Authorization: `Bearer ${token}`},
    params: {date},
  })
}

// =================== CREATE COMP-OFF REQUEST ===================

export const createCompOffRequest = async (data: CompOffRequestInput) => {
  const {token} = setupAxios()

  return axios.post(`${API_BASE_URL}/weekend-work/comp-off/request`, data, {
    headers: {Authorization: `Bearer ${token}`},
  })
}







